import { Component, Inject } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
    ActionTypes,
    AddOwnerActions,
    IsolateResourceActions,
    RemoveAccessActions,
    RemovePermissionsActions,
} from "src/app/models/api/playbooks/actions/actionTypes.enum";
import { PlaybookActionDefinitionPropertiesBase } from "src/app/models/api/playbooks/playbook.model";
import {
    PlaybooksActionDefinitionInput,
    PlaybooksActionInput,
    PlaybookSupportedActions,
} from "src/app/models/componentInputs/playbookActionInput.interface";
import { PlaybooksActionOutput } from "src/app/models/popups/outputs/playbookActionOutput.interface";
import { BeautifyDisplayStringPipe } from "src/app/pipes/beautify-display-string/beautify-display-string.pipe";
import { buildPlaybookActionTypeDisplayName } from "../../../labels/utils";
import { BehaviorSubject } from "rxjs";

interface ActionOptions {
    name: string;
    type: ActionTypes;
    tooltip?: string;
    disabled?: boolean;
}

@Component({
    selector: "app-playbook-action",
    templateUrl: "./playbook-action.component.html",
    styleUrls: ["./playbook-action.component.scss"],
})
export class PlaybookActionComponent {
    public playbookAction?: ActionTypes;
    public selectedAction?: ActionTypes;

    public actionTypes = ActionTypes;

    public fg = new UntypedFormGroup({});
    public actionOptions: ActionOptions[] = [];

    public actionProperties?: PlaybookActionDefinitionPropertiesBase;
    public actionFormInput: PlaybooksActionDefinitionInput;
    public limitedActionText?: string;
    private _supportedActions: PlaybookSupportedActions[];

    public showAddOwnerAction$ = new BehaviorSubject<boolean>(false);
    public showRemovePermissionsAction$ = new BehaviorSubject<boolean>(false);
    public showIsolateResourceAction$ = new BehaviorSubject<boolean>(false);
    public showRemoveAccessAction$ = new BehaviorSubject<boolean>(false);

    constructor(
        public dialogRef: MatDialogRef<PlaybookActionComponent>,
        @Inject(MAT_DIALOG_DATA) public actionInput: PlaybooksActionInput,
        private _beautifyPipe: BeautifyDisplayStringPipe,
    ) {
        this._supportedActions = actionInput.supportedActions;
        this.limitedActionText = actionInput.limitedActionsTooltip;
        this._createActionOptions();

        this.dialogRef.disableClose = true;
        this.dialogRef.backdropClick().subscribe(() => {
            this.closeWindow();
        });

        actionInput.action?.let((action) => {
            this.selectedAction = action.actionType;
            this.playbookAction = action.actionType;
            this.actionProperties = action.actionDefinitionProperties;

            this.actionFormInput = {
                platform: this.actionInput.platform,
                actionType: action.actionType,
                actionDefinitionProperties: action.actionDefinitionProperties,
            } as PlaybooksActionDefinitionInput;
        });

        this.updateActionVisibility(actionInput.action?.actionType);
    }

    public onActionSelected(action: ActionOptions) {
        this.fg = new UntypedFormGroup({});
        this._resetActionOnChange();

        if (action.disabled) return;

        this.actionFormInput = {
            platform: this.actionInput.platform,
            actionType: action.type,
            actionDefinitionProperties: this.actionProperties,
        } as PlaybooksActionDefinitionInput;

        this.selectedAction = action.type;
        this.updateActionVisibility(action.type);
    }

    public closeWindow() {
        this.dialogRef.close({} as PlaybooksActionOutput);
    }

    public createOrUpdate() {
        this.dialogRef.close({
            action: {
                actionType: this.selectedAction,
                actionDefinitionProperties: this.fg.value as PlaybookActionDefinitionPropertiesBase,
            },
        } as PlaybooksActionOutput);
    }

    private updateActionVisibility(action?: ActionTypes) {
        const isAddOwnerActionVisible = action != undefined && AddOwnerActions.includes(action);
        const isRemovePermissionsVisible = action != undefined && RemovePermissionsActions.includes(action);
        const isIsolateResourceActionVisible = action != undefined && IsolateResourceActions.includes(action);
        const isRemoveAccessActionVisible = action != undefined && RemoveAccessActions.includes(action);

        this.showAddOwnerAction$.next(isAddOwnerActionVisible);
        this.showRemovePermissionsAction$.next(isRemovePermissionsVisible);
        this.showIsolateResourceAction$.next(isIsolateResourceActionVisible);
        this.showRemoveAccessAction$.next(isRemoveAccessActionVisible);
    }

    private _createActionOptions() {
        this.actionOptions = this._supportedActions.map(
            (action) =>
                ({
                    type: action.actionType,
                    name: buildPlaybookActionTypeDisplayName(action.actionType),
                    tooltip: action.tooltip,
                    disabled: action.disabled,
                }) as ActionOptions,
        );

        const selection =
            this.playbookAction?.let((currentAction) => this.actionOptions.find((options) => options.type === currentAction)) ??
            this.actionOptions[0];

        this.onActionSelected(selection);
    }

    private _resetActionOnChange() {
        this.selectedAction = undefined;
        this.playbookAction = undefined;
        this.actionProperties = undefined;
    }
}
